import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Table, Badge, Button, Spinner, Alert, Tabs, Tab, Row, Col, Accordion, Form, Modal } from 'react-bootstrap';
import { getExtractionResults, compareExtractionResults, resolveDiscrepancies, retryExtractionJob, getExtractionJobFile, getExtractionJobPrompts, archiveExtractionJob, unarchiveExtractionJob, runCustomPrompt, markAsUnprocessable, retryExportExtractionJob, forceReprocessExtractionJob, getModelCostRates } from '../services/api/extractionApi';
import { getMetadataFieldsByCustomer } from '../services/api/metadataApi';
import LineItemsComparison from './LineItemsComparison';
import DiscrepanciesComparison from './DiscrepanciesComparison';
import ExtractionDetailsView from './ExtractionDetailsView';
import AugmentationJobDetails from './augmentation/AugmentationJobDetails';
import QuestionErrorCodeFields from './extraction/QuestionErrorCodeFields';
import { calculateCost, formatCost, sumCosts } from '../utils/costCalculator';
import { useAuth } from '../contexts/AuthContext';
import { useAugmentationJob } from '../hooks/useAugmentationJob';
import useTimedMessage from '../hooks/useTimedMessage';
import { usePageBreadcrumbs } from '../hooks/usePageBreadcrumbs';
import { EXTRACTION_JOB_STATUS, STATUS_DISPLAY, STATUS_VARIANTS } from '../constants/statusConstants';

const ExtractionJobDetail = () => {
  const { jobId } = useParams();
  const { hasPermission } = useAuth();

  // Breadcrumb registration - will update when job data loads
  const [job, setJob] = useState(null);
  const jobLabel = job?.fileName || job?.id || jobId || 'Loading...';
  usePageBreadcrumbs([
    { label: 'Extraction Jobs', path: '/extraction-jobs' },
    { label: jobLabel }
  ], [jobLabel]);

  const canViewResults = hasPermission('extraction_results', 'view');
  const canViewMetadata = hasPermission('extraction_metadata', 'view');
  const canViewComparison = hasPermission('extraction_comparison', 'view');
  const canViewDiscrepancies = hasPermission('extraction_discrepancies', 'view');
  const canViewLogs = hasPermission('extraction_logs', 'view');
  const canViewMetrics = hasPermission('extraction_metrics', 'view');
  const canViewPlayground = hasPermission('extraction_playground', 'view');
  const canViewPrompts = hasPermission('extraction_prompts', 'view');
  const canViewAugmentation = hasPermission('augmentation', 'manage') || hasPermission('augmentation', 'review');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, showError, setError] = useTimedMessage(3000);
  const [resolutions, setResolutions] = useState({});
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryingExport, setRetryingExport] = useState(false);
  const [forceReprocessing, setForceReprocessing] = useState(false);
  const [success, showSuccess, setSuccess] = useTimedMessage(3000);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [activeTab, setActiveTab] = useState('results');
  const [prompts, setPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [approvedModels, setApprovedModels] = useState([]);
  const [loadingApprovedModels, setLoadingApprovedModels] = useState(false);
  const [approvedModelsFetchFailed, setApprovedModelsFetchFailed] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [editedPrompt, setEditedPrompt] = useState('');
  const [runningPrompt, setRunningPrompt] = useState(false);
  const [playgroundResult, setPlaygroundResult] = useState(null);
  const [metadataNameMapping, setMetadataNameMapping] = useState({});
  const [showReviewCommentsModal, setShowReviewCommentsModal] = useState(false);
  const [showUnprocessableModal, setShowUnprocessableModal] = useState(false);
  const [unprocessableReason, setUnprocessableReason] = useState('');
  const [markingUnprocessable, setMarkingUnprocessable] = useState(false);
  const [questionCode, setQuestionCode] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [currentEditableResults, setCurrentEditableResults] = useState({});
  // Determine if we should fetch augmentation data based on job status
  const shouldFetchAugmentation = job && (
    job.statusId === EXTRACTION_JOB_STATUS.COMPLETED || 
    job.statusId === EXTRACTION_JOB_STATUS.NEEDS_REVIEW ||
    job.statusId === EXTRACTION_JOB_STATUS.AUGMENTING ||
    job.statusId === EXTRACTION_JOB_STATUS.AUGMENTATION_REVIEW ||
    job.statusId === EXTRACTION_JOB_STATUS.READY_FOR_EXPORT
  );

  // Determine if QuestionCode and ErrorCode fields should be visible
  const showQuestionErrorFields = job && (
    job.statusId === EXTRACTION_JOB_STATUS.NEEDS_REVIEW ||
    job.statusId === EXTRACTION_JOB_STATUS.COMPLETED ||
    job.statusId === EXTRACTION_JOB_STATUS.READY_FOR_EXPORT
  );

  // Use custom hook for augmentation job management, but only if we don't have augmentation data from the extraction job
  const {
    augmentationJob: hookAugmentationJob,
    loading: loadingAugmentation,
    retrying: retryingAugmentation,
    handleRetry: handleRetryAugmentation
  } = useAugmentationJob(jobId, shouldFetchAugmentation && !job?.augmentationJob);

  // Use augmentation data from extraction job if available, otherwise use hook data
  const augmentationJob = job?.augmentationJob || hookAugmentationJob;
  const loadingAugmentationData = job?.augmentationJob ? false : loadingAugmentation;


  const fetchPdfFile = useCallback(async () => {
    setLoadingPdf(true);
    try {
      const response = await getExtractionJobFile(jobId);
      const fileBlob = new Blob([response.data], { type: response.headers['content-type'] });
      const fileUrl = URL.createObjectURL(fileBlob);
      setPdfUrl(fileUrl);
    } catch (error) {
      console.error('Error fetching PDF file:', error);
      // Don't set an error state here, as the PDF might not be available
      // but we still want to show the rest of the job details
    } finally {
      setLoadingPdf(false);
    }
  }, [jobId]);

  const fetchPrompts = useCallback(async () => {
    setLoadingPrompts(true);
    try {
      const response = await getExtractionJobPrompts(jobId);
      setPrompts(response.data);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      // Don't set an error state here, as we still want to show the rest of the job details
    } finally {
      setLoadingPrompts(false);
    }
  }, [jobId]);

  const fetchApprovedModels = useCallback(async () => {
    setLoadingApprovedModels(true);
    try {
      const response = await getModelCostRates();
      setApprovedModels(response.data || []);
    } catch (error) {
      console.error('Error fetching model cost rates:', error);
      // Mark as attempted so we don't retry on 403 or other errors
      setApprovedModelsFetchFailed(true);
    } finally {
      setLoadingApprovedModels(false);
    }
  }, []);

  // Track if we've already loaded additional data for completed jobs
  const [loadedAdditionalData, setLoadedAdditionalData] = useState(false);
  
  const fetchJobDetails = useCallback(async () => {
    // Only show loading indicator on initial load
    if (!job) setLoading(true);
    
    try {
      const jobResponse = await getExtractionResults(jobId);
      const newJobData = jobResponse.data;
      
      // Check if the job data has actually changed before updating state
      const jobChanged = !job || job.statusId !== newJobData.statusId || job.id !== newJobData.id || job.lineItemsEditable !== newJobData.lineItemsEditable;
      
      if (jobChanged) {
        setJob(newJobData);

        // Use newJobData (fresh) for current status, stale job for previous status
        const isNowComplete = newJobData.statusId !== EXTRACTION_JOB_STATUS.PROCESSING && 
                              newJobData.statusId !== EXTRACTION_JOB_STATUS.REQUESTED;
        
        const wasPreviouslyComplete = job !== null &&
                                      job.statusId !== EXTRACTION_JOB_STATUS.PROCESSING && 
                                      job.statusId !== EXTRACTION_JOB_STATUS.REQUESTED;

        // Initialize QuestionCode and ErrorCode from job data
        setQuestionCode(newJobData.questionCode || '');
        setErrorCode(newJobData.errorCode || '');

        // For in-progress jobs, only fetch prompts to show progress
        if (newJobData.statusId === EXTRACTION_JOB_STATUS.PROCESSING || newJobData.statusId === EXTRACTION_JOB_STATUS.REQUESTED) {
          // Only fetch prompts if we don't already have them
          if (prompts.length === 0) {
            fetchPrompts();
          }
        }
        
        // Fetch PDF for all statuses if not already loaded
        if (!pdfUrl) {
          fetchPdfFile();
        }
        
        // Fetch comparison data when:
        // 1. Job just transitioned to a complete status (!wasPreviouslyComplete)
        // 2. Job is complete but we haven't loaded comparison yet (!loadedAdditionalData)
        if (isNowComplete && (!wasPreviouslyComplete || !loadedAdditionalData)) {
          // Mark that we've loaded additional data to prevent duplicate requests
          setLoadedAdditionalData(true);
          
          // Fetch comparison data (non-critical — may not exist for failed/augmentation-only jobs)
          try {
            const comparisonResponse = await compareExtractionResults(jobId);
            setComparison(comparisonResponse.data);
            
            // Initialize resolutions with the recommended values
            const initialResolutions = {};

          if (comparisonResponse.data.hasStoredDiscrepancyMap && comparisonResponse.data.discrepancyMap) {
            // New path: initialize resolutions from the backend-provided discrepancy map.
            // Only include entries where status === 99 (Unresolved), using resolvedValue as the initial value.
            const map = comparisonResponse.data.discrepancyMap;
            Object.keys(map).forEach(key => {
              const entry = map[key];
              if (entry.status === 99) {
                initialResolutions[key] = entry.resolvedValue;
              }
            });
          } else {
            // Legacy fallback: initialize resolutions from on-the-fly comparison data
            // when hasStoredDiscrepancyMap is false (pre-migration jobs).

            // Handle header discrepancies
            if (comparisonResponse.data.headerDiscrepancies) {
              Object.keys(comparisonResponse.data.headerDiscrepancies).forEach(field => {
                initialResolutions[field] = comparisonResponse.data.headerDiscrepancies[field].recommended || 
                                           comparisonResponse.data.headerDiscrepancies[field].model1Value;
              });
            }
            
            /**
             * @deprecated This toString() comparison logic for detecting line item discrepancies
             * is retained for backward compatibility with pre-migration jobs (hasStoredDiscrepancyMap: false).
             * New jobs use the backend-provided discrepancyMap instead. Use DiscrepancyMapBuilder on the backend.
             */
            // Handle line item discrepancies
            if (comparisonResponse.data.detailFields?.LineItems) {
              const lineItems = comparisonResponse.data.detailFields.LineItems;
              
              // Helper function to parse line items if they're JSON strings
              const parseLineItems = (items) => {
                if (!items) return [];
                if (Array.isArray(items)) return items;
                if (typeof items === 'string') {
                  try {
                    // Convert JSON number values to strings to preserve trailing zeroes
                    // (e.g., 65.00 stays as "65.00" instead of becoming JS number 65)
                    const preserved = items.replace(
                      /"([^"]+)"\s*:\s*(-?\d+(?:\.\d+)?)\s*(?=[,}\]])/g,
                      '"$1":"$2"'
                    );
                    const parsed = JSON.parse(preserved);
                    return Array.isArray(parsed) ? parsed : [];
                  } catch (e) {
                    console.error('Failed to parse line items:', e);
                    return [];
                  }
                }
                return [];
              };

              const model1Items = parseLineItems(lineItems.model1);
              const model2Items = parseLineItems(lineItems.model2);
              const model3Items = parseLineItems(lineItems.model3);
              const finalItems = parseLineItems(lineItems.final);
              
              // Process each line item
              for (let lineIndex = 0; lineIndex < Math.max(
                model1Items.length,
                model2Items.length,
                model3Items.length,
                finalItems.length
              ); lineIndex++) {
                
                // Get all keys for this line item
                const keySets = [];
                if (model1Items[lineIndex]) keySets.push(Object.keys(model1Items[lineIndex]));
                if (model2Items[lineIndex]) keySets.push(Object.keys(model2Items[lineIndex]));
                if (model3Items[lineIndex]) keySets.push(Object.keys(model3Items[lineIndex]));
                if (finalItems[lineIndex]) keySets.push(Object.keys(finalItems[lineIndex]));
                
                const keys = [...new Set(keySets.flat())];
                
                // For each field in the line item
                keys.forEach(field => {
                  const model1Value = model1Items[lineIndex]?.[field];
                  const model2Value = model2Items[lineIndex]?.[field];
                  const model3Value = model3Items[lineIndex]?.[field];
                  const finalValue = finalItems[lineIndex]?.[field];
                  
                  /** @deprecated Uses toString() comparison — replaced by backend discrepancyMap for new jobs */
                  // Check if there's a discrepancy
                  const hasDiscrepancy = (model1Value !== undefined && model2Value !== undefined && model1Value.toString() !== model2Value.toString()) ||
                                        (model1Value !== undefined && model3Value !== undefined && model1Value.toString() !== model3Value.toString()) ||
                                        (model2Value !== undefined && model3Value !== undefined && model2Value.toString() !== model3Value.toString()) ||
                                        (finalValue !== undefined && model1Value === undefined) ||
                                        (finalValue !== undefined && model2Value === undefined) ||
                                        (finalValue !== undefined && model3Value !== undefined && model3Value.toString() === "");
                  
                  if (hasDiscrepancy) {
                    const fieldId = `LineItems[${lineIndex}].${field}`;
                    
                    // Determine the recommended value (prefer final, then model3, then model2, then model1)
                    let recommendedValue = finalValue !== undefined ? finalValue : 
                                         model3Value !== undefined && model3Value !== "" ? model3Value :
                                         model2Value !== undefined ? model2Value :
                                         model1Value;
                    
                    initialResolutions[fieldId] = recommendedValue;
                  }
                });
              }
            }
          }
          
          setResolutions(initialResolutions);
          } catch (comparisonErr) {
            // Comparison data may not exist for jobs that failed during augmentation
            // or haven't completed the comparison step — this is non-critical
            console.warn('Comparison data not available:', comparisonErr.response?.status || comparisonErr.message);
          }
          
          // Fetch prompts and responses
          fetchPrompts();    
        }

        // Fetch metadata field configuration for display
        if (newJobData.customerId && (!job || job.customerId !== newJobData.customerId)) {
          fetchMetadataConfiguration(newJobData.customerId);
        }
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
      setError('Failed to load extraction job details');
    } finally {
      setLoading(false);
    }
  }, [jobId, job?.statusId, job?.id, job?.customerId, prompts.length, loadedAdditionalData, pdfUrl]);

  const fetchMetadataConfiguration = async (customerId) => {
    try {
      const response = await getMetadataFieldsByCustomer(customerId);
      const fields = response.data || [];
      const mapping = {};
      fields.forEach(field => {
        mapping[field.fieldKey] = field.name;
      });
      setMetadataNameMapping(mapping);
    } catch (error) {
      console.error('Error fetching metadata field configuration:', error);
    }
  };

  // Initial fetch only
  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);
  
  // Keep a ref to the latest fetchJobDetails to avoid stale closures in setInterval
  const fetchJobDetailsRef = useRef(fetchJobDetails);
  fetchJobDetailsRef.current = fetchJobDetails;

  // Separate effect for polling to avoid dependency issues
  useEffect(() => {
    // Don't start polling until we have job data
    if (!job) return;
    
    let pollInterval = null;
    
    // Only set up polling for in-progress jobs
    if (job.statusId === EXTRACTION_JOB_STATUS.REQUESTED || job.statusId === EXTRACTION_JOB_STATUS.PROCESSING) {
      pollInterval = setInterval(() => {
        fetchJobDetailsRef.current();
      }, 15000); // Poll every 15 seconds to reduce API load
    }
    
    // Clean up interval on unmount or when job status changes
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [job?.statusId]);
  
  // Separate useEffect for cleanup to avoid dependency cycle
  useEffect(() => {
    // Clean up the PDF URL when the component unmounts or when pdfUrl changes
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Fetch approved models when Playground or Metrics tab is accessed
  useEffect(() => {
    if ((activeTab === 'playground' || activeTab === 'metrics') && approvedModels.length === 0 && !loadingApprovedModels && !approvedModelsFetchFailed) {
      fetchApprovedModels();
    }
  }, [activeTab, approvedModels.length, loadingApprovedModels, approvedModelsFetchFailed, fetchApprovedModels]);

  // Look up cost rates for a model by its display name, falling back to modelId match
  // (when a model isn't in the approved list, the backend stores the raw AWS model ID as the name)
  const getModelRates = (modelName) => {
    if (!modelName || approvedModels.length === 0) return { input: null, output: null };
    const model = approvedModels.find(m => m.name === modelName || m.modelId === modelName);
    if (!model) return { input: null, output: null };
    return { input: model.costPer1MInputTokens, output: model.costPer1MOutputTokens };
  };

  const handleResolutionChange = (field, value) => {
    setResolutions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveResolutions = async () => {
    setSaving(true);
    try {
      // Build resolutions from the current editable results (synced from ExtractionDetailsView)
      // so the top-level save button sends the user's actual edits, not stale model values
      const currentResolutions = {};
      if (currentEditableResults && Object.keys(currentEditableResults).length > 0) {
        Object.keys(currentEditableResults).forEach(key => {
          if (key !== 'LineItems') {
            currentResolutions[key] = currentEditableResults[key];
          }
        });
        if (currentEditableResults.LineItems) {
          currentEditableResults.LineItems.forEach((lineItem, lineIndex) => {
            Object.keys(lineItem).forEach(fieldKey => {
              currentResolutions[`LineItems[${lineIndex}].${fieldKey}`] = lineItem[fieldKey];
            });
          });
        }
      }

      // Use the live editable results if available, otherwise fall back to resolutions state
      const resolutionsToSend = Object.keys(currentResolutions).length > 0 ? currentResolutions : resolutions;

      await resolveDiscrepancies(jobId, {
        resolutions: resolutionsToSend,
        questionCode,
        errorCode
      });
      setSuccess('Changes saved successfully');
      
      // Reset loadedAdditionalData to force refresh of comparison data
      setLoadedAdditionalData(false);
      
      // Refresh job details
      await fetchJobDetails();
      
      // Switch to ExtractionResults tab when status becomes COMPLETED
      setActiveTab('results');
    } catch (error) {
      console.error('Error saving changes:', error);
      showError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    try {
      const response = await archiveExtractionJob(jobId);

      // Defensive handling of response structure
      if (!response) {
        throw new Error('No response received from server');
      }
      
      // Handle both response.data and direct response structures
      const responseData = response.data || response;
      
      // Check if the operation was successful
      if (responseData.success === false) {
        throw new Error(responseData.message || 'Archive operation failed');
      }
      
      const isArchived = responseData.isArchived ?? true;
      
      setJob(prev => ({ ...prev, isArchived }));
      showSuccess('Job archived successfully');
    } catch (error) {
      console.error('Error archiving job:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to archive job';
      showError(errorMessage);
    }
  };

  const handleUnarchive = async () => {
    try {
      const response = await unarchiveExtractionJob(jobId);
      console.log('Unarchive response:', response);
      
      // Defensive handling of response structure
      if (!response) {
        throw new Error('No response received from server');
      }
      
      // Handle both response.data and direct response structures
      const responseData = response.data || response;
      
      // Check if the operation was successful
      if (responseData.success === false) {
        throw new Error(responseData.message || 'Unarchive operation failed');
      }
      
      const isArchived = responseData.isArchived ?? false;
      
      setJob(prev => ({ ...prev, isArchived }));
      showSuccess('Job restored successfully');
    } catch (error) {
      console.error('Error unarchiving job:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to unarchive job';
      showError(errorMessage);
    }
  };

  const handleRetryJob = async () => {
    setRetrying(true);
    try {
      const response = await retryExtractionJob(jobId);
      showSuccess('Job retry initiated successfully. The job will be reprocessed.', 5000);
      
      // Reset loadedAdditionalData to force refresh of job data
      setLoadedAdditionalData(false);
      
      // Refresh job details to show updated status
      await fetchJobDetails();
    } catch (error) {
      console.error('Error retrying job:', error);
      showError('Failed to retry job: ' + (error.response?.data || error.message), 5000);
    } finally {
      setRetrying(false);
    }
  };
  
  const handleRetryExport = async () => {
    setRetryingExport(true);
    try {
      const response = await retryExportExtractionJob(jobId);
      showSuccess('Export retry initiated successfully. The job will be re-exported.', 5000);
      
      // Reset loadedAdditionalData to force refresh of job data
      setLoadedAdditionalData(false);
      
      // Refresh job details to show updated status
      await fetchJobDetails();
    } catch (error) {
      console.error('Error retrying export:', error);
      showError('Failed to retry export: ' + (error.response?.data || error.message), 5000);
    } finally {
      setRetryingExport(false);
    }
  };

  const handleForceReprocess = async () => {
    setForceReprocessing(true);
    try {
      await forceReprocessExtractionJob(jobId);
      showSuccess('Force reprocess initiated successfully. The job will be reprocessed.', 5000);
      
      // Reset loadedAdditionalData to force refresh of job data
      setLoadedAdditionalData(false);
      
      // Refresh job details to show updated status
      await fetchJobDetails();
    } catch (error) {
      console.error('Error force reprocessing job:', error);
      showError('Failed to force reprocess job: ' + (error.response?.data || error.message), 5000);
    } finally {
      setForceReprocessing(false);
    }
  };

  const handleMarkAsUnprocessable = async () => {
    if (!unprocessableReason.trim()) {
      setError('Please provide a reason');
      return;
    }
    
    setMarkingUnprocessable(true);
    try {
      await markAsUnprocessable(jobId, unprocessableReason);
      showSuccess('Job marked as unprocessable successfully', 5000);
      setShowUnprocessableModal(false);
      setUnprocessableReason('');
      
      // Reset loadedAdditionalData to force refresh of job data
      setLoadedAdditionalData(false);
      
      // Refresh job details to show updated status
      await fetchJobDetails();
    } catch (error) {
      console.error('Error marking job as unprocessable:', error);
      showError('Failed to mark job as unprocessable: ' + (error.response?.data?.message || error.message), 5000);
    } finally {
      setMarkingUnprocessable(false);
    }
  };

  // Wrapper for augmentation retry to handle success/error messages
  const handleAugmentationRetry = async () => {
    const result = await handleRetryAugmentation();
    if (result.success) {
      showSuccess('Augmentation job retry initiated successfully. The job will be reprocessed.', 5000);
    } else {
      showError('Failed to retry augmentation job: ' + result.error, 5000);
    }
  };

  const handleRunPrompt = async () => {
    if (!selectedModel || !editedPrompt) {
      setError('Please select a model and enter a prompt');
      return;
    }

    setRunningPrompt(true);
    setPlaygroundResult(null);
    
    try {
      const response = await runCustomPrompt(jobId, {
        modelId: selectedModel,
        prompt: editedPrompt
      });
      
      setPlaygroundResult(response.data);
    } catch (error) {
      console.error('Error running custom prompt:', error);
      setPlaygroundResult({
        error: 'Failed to run prompt: ' + (error.response?.data || error.message)
      });
    } finally {
      setRunningPrompt(false);
    }
  };

  const getStatusBadge = (status) => {
    const variant = STATUS_VARIANTS[status] || 'secondary';
    const text = STATUS_DISPLAY[status] || status;
    return <Badge bg={variant}>{text}</Badge>;
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading extraction job details...</p>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (!job) {
    return <Alert variant="warning">Job not found</Alert>;
  }

  return (
    <div>
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            Extraction Job: {job.id}
            {(job.customerName || job.rulesetName) && (
              <span className="ms-3">
                {job.customerName && <span>Customer: {job.customerName}</span>}
                {job.customerName && job.rulesetName && <span className="mx-2">•</span>}
                {job.rulesetName && <span>Ruleset: {job.rulesetName}</span>}
              </span>
            )}
          </h5>
          <div className="d-flex gap-2">
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={fetchJobDetails}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>Refresh
            </Button>
            {!job.isArchived ? (
              <Button variant="outline-secondary" size="sm" onClick={handleArchive}>
                <i className="bi bi-archive me-1"></i>Archive
              </Button>
            ) : (
              <Button variant="outline-success" size="sm" onClick={handleUnarchive}>
                <i className="bi bi-archive-fill me-1"></i>Unarchive
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {success && <Alert variant="success">{success}</Alert>}
          
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-4">
              <div><strong>Status:</strong> {getStatusBadge(job.statusId)}</div>
              <div><strong>Created:</strong> {new Date(job.createdAt).toLocaleString()}</div>
              <div><strong>File Name:</strong> {job.fileName}</div>
            </div>
            <div className="d-flex gap-2">
              {(job.statusId === EXTRACTION_JOB_STATUS.FAILED || job.statusId === EXTRACTION_JOB_STATUS.UNPROCESSABLE) && (
                <Button 
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRetryJob}
                  disabled={retrying}
                >
                  {retrying ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Retry Extraction
                    </>
                  )}
                </Button>
              )}
              {(job.statusId === EXTRACTION_JOB_STATUS.EXPORT_FAILED ||
                job.statusId === EXTRACTION_JOB_STATUS.EXPORTED ||
                job.statusId === EXTRACTION_JOB_STATUS.EXPORTING) && (
                <Button 
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRetryExport}
                  disabled={retryingExport}
                >
                  {retryingExport ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {job.statusId === EXTRACTION_JOB_STATUS.EXPORTING ? 'Re-Exporting...' : 'Retrying Export...'}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      {job.statusId === EXTRACTION_JOB_STATUS.EXPORTING ? 'Re-Export' : 'Retry Export'}
                    </>
                  )}
                </Button>
              )}
              {job.statusId === EXTRACTION_JOB_STATUS.PROCESSING && (
                <Button 
                  variant="outline-warning"
                  size="sm"
                  onClick={handleForceReprocess}
                  disabled={forceReprocessing}
                >
                  {forceReprocessing ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Force Reprocessing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Force Reprocess
                    </>
                  )}
                </Button>
              )}
              {(job.statusId === EXTRACTION_JOB_STATUS.FAILED ) && (
                <Button 
                  variant="outline-warning"
                  size="sm"
                  onClick={() => setShowUnprocessableModal(true)}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Mark Unprocessable
                </Button>
              )}
              {job.unprocessableReason && (
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={() => {
                    setUnprocessableReason(job.unprocessableReason);
                    setShowUnprocessableModal(true);
                  }}
                >
                  <i className="bi bi-info-circle me-1"></i>
                  View Reason
                </Button>
              )}
              {job.reviewComments && (
                <Button 
                  variant="outline-info" 
                  size="sm"
                  onClick={() => setShowReviewCommentsModal(true)}
                >
                  <i className="bi bi-chat-text me-1"></i>
                  View Review Comments
                </Button>
              )}
            </div>
          </div>

          {/* QuestionCode and ErrorCode Fields - always visible, editable only in specific statuses */}
          <QuestionErrorCodeFields
            questionCode={questionCode}
            errorCode={errorCode}
            onQuestionCodeChange={setQuestionCode}
            onErrorCodeChange={setErrorCode}
            onSave={handleSaveResolutions}
            disabled={false}
            saving={saving}
            readOnly={!showQuestionErrorFields}
          />

          {/* Augmentation Status Section */}
          {(job.statusId === EXTRACTION_JOB_STATUS.COMPLETED || 
            job.statusId === EXTRACTION_JOB_STATUS.NEEDS_REVIEW ||
            job.statusId === EXTRACTION_JOB_STATUS.AUGMENTING ||
            job.statusId === EXTRACTION_JOB_STATUS.AUGMENTATION_REVIEW ||
            job.statusId === EXTRACTION_JOB_STATUS.READY_FOR_EXPORT) && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Augmentation Status:</strong>{' '}
                  {loadingAugmentationData ? (
                    <Spinner animation="border" size="sm" className="ms-2" />
                  ) : augmentationJob ? (
                    <>
                      <Badge 
                        bg={
                          augmentationJob.status === 'COMPLETED' ? 'success' :
                          augmentationJob.status === 'NEEDS_REVIEW_AUGMENTATION' ? 'warning' :
                          augmentationJob.status === 'FAILED' ? 'danger' :
                          augmentationJob.status === 'PROCESSING' ? 'primary' :
                          'secondary'
                        }
                        className="ms-2"
                      >
                        {augmentationJob.status}
                      </Badge>
                      {augmentationJob.statusReason && (
                        <span className="ms-2 text-muted">({augmentationJob.statusReason})</span>
                      )}
                      {augmentationJob.rulesExecuted && (
                        <span className="ms-2 text-muted">
                          • {augmentationJob.rulesExecuted} rules executed
                        </span>
                      )}
                      {augmentationJob.totalProcessingTimeMs && (
                        <span className="ms-2 text-muted">
                          • {(augmentationJob.totalProcessingTimeMs / 1000).toFixed(2)}s
                        </span>
                      )}
                    </>
                  ) : (
                    <Badge bg="secondary" className="ms-2">Not Started</Badge>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {augmentationJob && augmentationJob.status === 'FAILED' && (
                    <Button 
                      variant="outline-primary"
                      size="sm"
                      onClick={handleAugmentationRetry}
                      disabled={retryingAugmentation}
                    >
                      {retryingAugmentation ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Retrying...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-clockwise me-1"></i>
                          Retry Augmentation
                        </>
                      )}
                    </Button>
                  )}
                  {augmentationJob && augmentationJob.status === 'NEEDS_REVIEW_AUGMENTATION' && (
                    <Button 
                      as={Link}
                      to={`/augmentation-review/${augmentationJob.id}`}
                      variant="warning"
                      size="sm"
                    >
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      Review Required
                    </Button>
                  )}
                </div>
              </div>
            </Alert>
          )}

          <Tabs 
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k);
              // Fetch comparison data on demand if switching to comparison/discrepancies tab and data isn't loaded
              if ((k === 'comparison' || k === 'discrepancies') && !comparison && job) {
                const isComplete = job.statusId !== EXTRACTION_JOB_STATUS.PROCESSING && 
                                   job.statusId !== EXTRACTION_JOB_STATUS.REQUESTED;
                if (isComplete) {
                  compareExtractionResults(jobId).then(res => {
                    setComparison(res.data);
                  }).catch(err => {
                    console.error('Error fetching comparison data on tab switch:', err);
                  });
                }
              }
            }}
            className="mb-3">
            {canViewResults && (
              <Tab eventKey="results" title="Extraction Results">
                <ExtractionDetailsView 
                  job={job} 
                  onJobUpdate={() => fetchJobDetails()}
                  showPdfViewer={true}
                  pdfUrl={pdfUrl}
                  loadingPdf={loadingPdf}
                  comparison={comparison}
                  questionCode={questionCode}
                  errorCode={errorCode}
                  onEditableResultsChange={setCurrentEditableResults}
                />
              </Tab>
            )}
            
            {canViewAugmentation && (job.statusId === EXTRACTION_JOB_STATUS.COMPLETED || 
                                          job.statusId === EXTRACTION_JOB_STATUS.NEEDS_REVIEW ||
                                          job.statusId === EXTRACTION_JOB_STATUS.AUGMENTING ||
                                          job.statusId === EXTRACTION_JOB_STATUS.AUGMENTATION_REVIEW ||
                                          job.statusId === EXTRACTION_JOB_STATUS.READY_FOR_EXPORT) && (
              <Tab eventKey="augmentation" title="Augmentation">
                <AugmentationJobDetails
                  augmentationJob={augmentationJob}
                  loading={loadingAugmentationData}
                  error={augmentationJob === null && !loadingAugmentationData ? 'No augmentation job found for this extraction' : null}
                  onRetry={handleAugmentationRetry}
                  retrying={retryingAugmentation}
                />
              </Tab>
            )}
            
            {canViewMetadata && job.invoiceMetadata && Object.keys(job.invoiceMetadata).length > 0 && (
              <Tab eventKey="metadata" title="Metadata">
                <div className="mt-3">
                  <h6>Invoice Metadata</h6>
                  <div className="table-scroll-wrapper">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(job.invoiceMetadata).map(([field, value]) => (
                          <tr key={field}>
                            <td>{metadataNameMapping[field] || field}</td>
                            <td>{value !== null && value !== undefined ? value.toString() : 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Tab>
            )}
            
            {canViewComparison && (
              <Tab eventKey="comparison" title="Model Comparison">
              {comparison ? (
                <Row>
                  <Col md={6}>
                    <div className="extraction-results">
                      <h6>Header Fields Comparison</h6>
                      <div className="table-scroll-wrapper">
                        <Table striped bordered hover>
                          <thead>
                            <tr>
                              <th>Field</th>
                              <th>{job.model1}</th>
                              <th>{job.model2}</th>
                              {job.model3 && <th>{job.model3}</th>}
                              <th>Final Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(comparison.headerFields || {}).map(([field, values]) => (
                              field !== 'LineItems' ? (
                                <tr key={field} className={comparison.headerDiscrepancies?.[field] ? 'table-warning' : ''}>
                                  <td>{field}</td>
                                  <td className={values.model1 !== values.final ? 'bg-warning' : ''}>{values.model1 !== null && values.model1 !== undefined ? values.model1.toString() : 'N/A'}</td>
                                  <td className={values.model2 !== values.final ? 'bg-warning' : ''}>{values.model2 !== null && values.model2 !== undefined ? values.model2.toString() : 'N/A'}</td>
                                  {job.model3 && <td className={values.model3 !== values.final ? 'bg-warning' : ''}>{values.model3 !== null && values.model3 !== undefined ? values.model3.toString() : 'N/A'}</td>}
                                  <td>{values.final !== null && values.final !== undefined ? values.final.toString() : 'N/A'}</td>
                                </tr>
                              ) : null
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {comparison.detailFields && comparison.detailFields.LineItems && (
                        <div className="mt-4">
                          <LineItemsComparison 
                            model1Name={job.model1}
                            model2Name={job.model2}
                            model3Name={job.model3}
                            lineItems={comparison.detailFields.LineItems}
                          />
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="pdf-viewer">
                      {loadingPdf ? (
                        <div className="text-center my-5">
                          <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading PDF...</span>
                          </Spinner>
                          <p className="mt-2">Loading invoice document...</p>
                        </div>
                      ) : pdfUrl ? (
                        <iframe
                          src={pdfUrl}
                          title="Invoice PDF"
                          width="100%"
                          height="100%"
                        />
                      ) : (
                        <div className="text-center my-5">
                          <Alert variant="info">
                            Invoice document not available for preview.
                          </Alert>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              ) : (
                <div className="text-center my-4">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Loading comparison data...
                </div>
              )}
              </Tab>
            )}
            
            {canViewDiscrepancies && job.statusId === EXTRACTION_JOB_STATUS.NEEDS_REVIEW && comparison && (
              <Tab eventKey="discrepancies" title="Resolve Discrepancies">
                <Alert variant="info" className="mb-3">
                  The following fields have discrepancies between the models. Please review and select the correct value.
                </Alert>
                
                <Row>
                  <Col md={6}>
                    <div className="extraction-results">
                      <DiscrepanciesComparison
                        model1Name={job.model1}
                        model2Name={job.model2}
                        model3Name={job.model3}
                        headerDiscrepancies={comparison.headerDiscrepancies}
                        lineItemDiscrepancies={comparison.detailFields?.LineItems}
                        resolutions={resolutions}
                        onResolutionChange={handleResolutionChange}
                        hasStoredDiscrepancyMap={comparison.hasStoredDiscrepancyMap || false}
                      />
                      
                      <div className="d-flex justify-content-end mt-3">
                        <Button 
                          variant="primary" 
                          onClick={handleSaveResolutions}
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              Saving...
                            </>
                          ) : (
                            'Save Resolutions'
                          )}
                        </Button>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="pdf-viewer">
                      {loadingPdf ? (
                        <div className="text-center my-5">
                          <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading PDF...</span>
                          </Spinner>
                          <p className="mt-2">Loading invoice document...</p>
                        </div>
                      ) : pdfUrl ? (
                        <iframe
                          src={pdfUrl}
                          title="Invoice PDF"
                          width="100%"
                          height="100%"
                        />
                      ) : (
                        <div className="text-center my-5">
                          <Alert variant="info">
                            Invoice document not available for preview.
                          </Alert>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </Tab>
            )}
            
            {canViewLogs && (
              <Tab eventKey="logs" title="Processing Logs">
              <div className="mt-3">
                <h6>Processing Logs</h6>
                {job.logsJson ? (
                  <div className="bg-dark text-light p-3 rounded" style={{ maxHeight: '400px', overflow: 'auto' }}>
                    {(() => {
                      try {
                        const logs = JSON.parse(job.logsJson);
                        return logs.map((log, index) => (
                          <div key={index} className="mb-1">
                            <small>
                              [{new Date(log.Timestamp).toLocaleString()}] [{log.Level}] {log.Message}
                            </small>
                          </div>
                        ));
                      } catch (error) {
                        return <p className="text-warning">Error parsing logs: {error.message}</p>;
                      }
                    })()}
                  </div>
                ) : (
                  <p>No logs available</p>
                )}
              </div>
              </Tab>
            )}

            {canViewMetrics && (
              <Tab eventKey="metrics" title="Processing Metrics">
              <div className="mt-3">
                <h6>Model Processing Metrics</h6>
                
                <div className="table-scroll-wrapper">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Model</th>
                        <th>Input Tokens</th>
                        <th>Output Tokens</th>
                        <th>Processing Time</th>
                        <th>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{job.model1}</td>
                        <td>{job.model1InputTokenCount ? job.model1InputTokenCount.toLocaleString() : 'N/A'}</td>
                        <td>{job.model1OutputTokenCount ? job.model1OutputTokenCount.toLocaleString() : 'N/A'}</td>
                        <td>{job.model1ProcessingTimeMs ?
                          `${(job.model1ProcessingTimeMs / 1000).toFixed(2)}s` :
                          'N/A'}</td>
                        <td>{job.model1InputTokenCount && job.model1OutputTokenCount ?
                          formatCost(calculateCost(job.model1InputTokenCount, job.model1OutputTokenCount, getModelRates(job.model1).input, getModelRates(job.model1).output)) :
                          'N/A'}</td>
                      </tr>
                      <tr>
                        <td>{job.model2}</td>
                        <td>{job.model2InputTokenCount ? job.model2InputTokenCount.toLocaleString() : 'N/A'}</td>
                        <td>{job.model2OutputTokenCount ? job.model2OutputTokenCount.toLocaleString() : 'N/A'}</td>
                        
                        <td>{job.model2ProcessingTimeMs ?
                          `${(job.model2ProcessingTimeMs / 1000).toFixed(2)}s` :
                          'N/A'}</td>
                        <td>{job.model2InputTokenCount && job.model2OutputTokenCount ?
                          formatCost(calculateCost(job.model2InputTokenCount, job.model2OutputTokenCount, getModelRates(job.model2).input, getModelRates(job.model2).output)) :
                          'N/A'}</td>
                      </tr>
                      <tr>
                        <td>{job.model3}</td>
                        <td>{job.model3InputTokenCount ? job.model3InputTokenCount.toLocaleString() : 'N/A'}</td>
                        <td>{job.model3OutputTokenCount ? job.model3OutputTokenCount.toLocaleString() : 'N/A'}</td>
                        
                        <td>{job.model3ProcessingTimeMs ?
                          `${(job.model3ProcessingTimeMs / 1000).toFixed(2)}s` :
                          'N/A'}</td>
                        <td>{job.model3InputTokenCount && job.model3OutputTokenCount ?
                          formatCost(calculateCost(job.model3InputTokenCount, job.model3OutputTokenCount, getModelRates(job.model3).input, getModelRates(job.model3).output)) :
                          'N/A'}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td><strong>{job.totalInputTokenCount ? job.totalInputTokenCount.toLocaleString() : 0}</strong></td>
                        <td><strong>{job.totalOutputTokenCount ? job.totalOutputTokenCount.toLocaleString() : 0}</strong></td>
                        <td><strong>{((job.totalProcessingTimeMs || 0) / 1000).toFixed(2)}s</strong></td>
                        <td><strong>{formatCost(
                          sumCosts(
                            calculateCost(job.model1InputTokenCount, job.model1OutputTokenCount, getModelRates(job.model1).input, getModelRates(job.model1).output),
                            calculateCost(job.model2InputTokenCount, job.model2OutputTokenCount, getModelRates(job.model2).input, getModelRates(job.model2).output),
                            calculateCost(job.model3InputTokenCount, job.model3OutputTokenCount, getModelRates(job.model3).input, getModelRates(job.model3).output)
                          )
                        )}</strong></td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </div>
              </Tab>
            )}
            
            {canViewPlayground && (
              <Tab eventKey="playground" title="Playground">
 <Card>
                        <Card.Header>
                          <h6 className="mb-0">Prompt Playground</h6>
                        </Card.Header>
                        <Card.Body>
                          <Row>
                            <Col md={12}>
                              <Form>
                                <Form.Group className="mb-3">
                                  <Form.Label>Select Base Prompt</Form.Label>
                                  <Form.Select 
                                    value={selectedPromptId} 
                                    onChange={(e) => {
                                      const promptId = e.target.value;
                                      setSelectedPromptId(promptId);
                                      const prompt = prompts.find(p => p.id.toString() === promptId);
                                      if (prompt) {
                                        setEditedPrompt(prompt.prompt);
                                        setSelectedModel(prompt.modelId);
                                      }
                                    }}
                                  >
                                    <option value="">Select a prompt...</option>
                                    {prompts.map(prompt => {
                                      const model = approvedModels.find(m => m.modelId === prompt.modelId);
                                      const displayName = model ? model.name : prompt.modelId;
                                      return (
                                        <option key={prompt.id} value={prompt.id}>
                                          {displayName} - {new Date(prompt.createdAt).toLocaleString()}
                                        </option>
                                      );
                                    })}
                                  </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Select Model</Form.Label>
                                  <Form.Select 
                                    value={selectedModel} 
                                    onChange={(e) => setSelectedModel(e.target.value)}
                                    disabled={loadingApprovedModels}
                                  >
                                    <option value="">Select a model...</option>
                                    {approvedModels.map(model => (
                                      <option key={model.modelId} value={model.modelId}>{model.name}</option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                  <Form.Label>Edit Prompt</Form.Label>
                                  <Form.Control 
                                    as="textarea" 
                                    rows={10} 
                                    value={editedPrompt}
                                    onChange={(e) => setEditedPrompt(e.target.value)}
                                    style={{ fontFamily: 'monospace' }}
                                  />
                                </Form.Group>
                                <div className="d-flex justify-content-end">
                                  <Button 
                                    variant="primary"
                                    onClick={handleRunPrompt}
                                    disabled={!selectedModel || !editedPrompt || runningPrompt}
                                  >
                                    {runningPrompt ? (
                                      <>
                                        <Spinner
                                          as="span"
                                          animation="border"
                                          size="sm"
                                          role="status"
                                          aria-hidden="true"
                                          className="me-2"
                                        />
                                        Running...
                                      </>
                                    ) : (
                                      'Run Prompt'
                                    )}
                                  </Button>
                                </div>
                              </Form>
                            </Col>
                          </Row>
                          {playgroundResult && (
                            <Row className="mt-4">
                              <Col md={12}>
                                <h6>Result</h6>
                                <div className="bg-light p-3" style={{ whiteSpace: 'pre-wrap', maxHeight: '400px', overflow: 'auto' }}>
                                  {playgroundResult.rawResponse ? (
                                    playgroundResult.rawResponse
                                  ) : playgroundResult.resultsJson ? (
                                    <pre>{JSON.stringify(JSON.parse(playgroundResult.resultsJson), null, 2)}</pre>
                                  ) : (
                                    "No response data available"
                                  )}
                                </div>
                                {playgroundResult.error && (
                                  <Alert variant="danger" className="mt-3">
                                    {playgroundResult.error}
                                  </Alert>
                                )}
                                {playgroundResult.metrics && (
                                  <Table size="sm" bordered className="mt-3">
                                    <tbody>
                                      <tr>
                                        <th>Input Tokens</th>
                                        <td>{playgroundResult.metrics.inputTokens}</td>
                                        <th>Output Tokens</th>
                                        <td>{playgroundResult.metrics.outputTokens}</td>
                                        <th>Processing Time</th>
                                        <td>{(playgroundResult.metrics.processingTimeMs / 1000).toFixed(2)}s</td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                )}
                              </Col>
                            </Row>
                          )}
                        </Card.Body>
                      </Card>
              </Tab>
            )}
            
            {canViewPrompts && (
              <Tab eventKey="prompts" title="Prompts & Responses">
              <div className="mt-3">
                <h6>Model Prompts and Responses</h6>
                
                {loadingPrompts ? (
                  <div className="text-center my-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading prompts and responses...
                  </div>
                ) : prompts && prompts.length > 0 ? (
                  <>
                    <div className="mb-4">

                    </div>
                    <Accordion defaultActiveKey="0">
                      {prompts.map((prompt, index) => (
                        <Accordion.Item key={prompt.id} eventKey={index.toString()}>
                          <Accordion.Header>
                            <div>
                              <strong>Model: {prompt.modelId}</strong>
                              <span className="ms-3 text-muted">
                                {new Date(prompt.createdAt).toLocaleString()}
                              </span>
                              <span className="ms-3 badge bg-info">
                                {prompt.inputTokenCount + prompt.outputTokenCount} tokens
                              </span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body>
                            <Row>
                              <Col md={6}>
                                <h6>Prompt</h6>
                                <div className="bg-light p-3 mb-3" style={{ whiteSpace: 'pre-wrap', maxHeight: '400px', overflow: 'auto' }}>
                                  {prompt.prompt}
                                </div>
                              </Col>
                              <Col md={6}>
                                <h6>Response</h6>
                                <div className="bg-light p-3" style={{ whiteSpace: 'pre-wrap', maxHeight: '400px', overflow: 'auto' }}>
                                  {prompt.rawResponse ? (
                                    prompt.rawResponse
                                  ) : prompt.resultsJson ? (
                                    <div>
                                      <p className="text-muted mb-2">Raw response not available. Showing extracted data:</p>
                                      <pre>{JSON.stringify(JSON.parse(prompt.resultsJson), null, 2)}</pre>
                                    </div>
                                  ) : (
                                    "No response data available"
                                  )}
                                </div>
                              </Col>
                            </Row>
                            <div className="mt-3">
                              <h6>Extracted Data</h6>
                              {prompt.resultsJson ? (
                                <div className="bg-light p-3" style={{ maxHeight: '200px', overflow: 'auto' }}>
                                  <pre>{JSON.stringify(JSON.parse(prompt.resultsJson), null, 2)}</pre>
                                </div>
                              ) : (
                                <p>No structured data extracted</p>
                              )}
                            </div>
                            <div className="mt-3">
                              <Table size="sm" bordered>
                                <tbody>
                                  <tr>
                                    <th>Input Tokens</th>
                                    <td>{prompt.inputTokenCount}</td>
                                    <th>Output Tokens</th>
                                    <td>{prompt.outputTokenCount}</td>
                                    <th>Processing Time</th>
                                    <td>{(prompt.processingTimeMs / 1000).toFixed(2)}s</td>
                                  </tr>
                                </tbody>
                              </Table>
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </>
                ) : (
                  <Alert variant="info">
                    No prompts and responses available for this job.
                  </Alert>
                )}
              </div>
              </Tab>
            )}
          </Tabs>
        </Card.Body>
      </Card>
      
      {/* Review Comments Modal */}
      <Modal show={showReviewCommentsModal} onHide={() => setShowReviewCommentsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Review Comments</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{job?.reviewComments}</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReviewCommentsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Mark as Unprocessable Modal */}
      <Modal show={showUnprocessableModal} onHide={() => { setShowUnprocessableModal(false); setUnprocessableReason(''); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Mark as Unprocessable</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {job?.unprocessableReason ? (
            <div>
              <Alert variant="info">This job has already been marked as unprocessable.</Alert>
              <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                <strong>Reason:</strong>
                <p className="mb-0 mt-2" style={{ whiteSpace: 'pre-wrap' }}>{job.unprocessableReason}</p>
              </div>
            </div>
          ) : (
            <Form>
              <Form.Group>
                <Form.Label>Please provide a reason for marking this job as unprocessable:</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={unprocessableReason}
                  onChange={(e) => setUnprocessableReason(e.target.value)}
                  placeholder="Enter reason (required)"
                  maxLength={2000}
                />
                <Form.Text className="text-muted">
                  {unprocessableReason.length}/2000 characters
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowUnprocessableModal(false); setUnprocessableReason(''); }}>
            Close
          </Button>
          {!job?.unprocessableReason && (
            <Button 
              variant="warning" 
              onClick={handleMarkAsUnprocessable}
              disabled={markingUnprocessable || !unprocessableReason.trim()}
            >
              {markingUnprocessable ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Marking...
                </>
              ) : (
                'Mark as Unprocessable'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExtractionJobDetail;